// WishDetail — `/wishes/:id` route (W33.5 cross-module polish + W34.2
// mobile affordances).
//
// Per `docs/spec/wishlist/DESIGN.md §6`:
//   - 6 block type renderers (notes / checklist / progress / budget / link / photo)
//     via `<BlockRenderer>` (each block type's own component).
//   - Completion CTA — opens CompletionDialog with the 撕信 animation
//     (per `docs/spec/DESIGN.md §9 pattern 3`).
//   - Linked diaries section (reverse-lookup via
//     `GET /api/v1/source/wishlist/{wishId}/diary`).
//
// W34.2 polish (this commit):
//   - Mobile (useIsMobile) budget / progress blocks open `MobileLogSheet`
//     on tap; notes / checklist / link / photo blocks have no log
//     affordance (per DATA_MODEL §5.5).
//   - Detail page BlockBody renders checklist / link / photo sub-tables
//     (W34.2.1) — the inline content lives below the block header.
//   - Block highlight via `?block=:id` URL param (per W34.2.1 follow-up
//     to BlockSettingsPanel "管理" button).

import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { PageContainer } from "@/components/layout/PageContainer";
import { PageHero } from "@/components/primitives/PageHero";
import { PageErrorBoundary } from "@/components/ErrorBoundary";
import { LoadingState } from "@/components/primitives/LoadingState";
import { ErrorState } from "@/components/primitives/ErrorState";
import { CompletionDialog } from "@/components/wish/CompletionDialog";
import { MobileLogSheet } from "@/components/wish/MobileLogSheet";
import { WishDetailActionBar } from "@/components/wish/WishDetailActionBar";
import { WishChatPanel } from "@/components/wish/WishChatPanel";
import { WishRemindersPanel } from "@/components/wish/WishRemindersPanel";
import { WishTimelinePanel } from "@/components/wish/WishTimelinePanel";
import { WishPhasesPanel } from "@/components/wish/WishPhasesPanel";
import { WishEventTimeline } from "@/components/wish/WishEventTimeline";
// W50.3 D-3 — the section moved out of this file so it could carry the link /
// unlink controls without pushing the route past its 15 KB ceiling.
import { WishDiaryLinks } from "@/components/wish/WishDiaryLinks";
import { useIsMobile } from "@/lib/hooks/useIsMobile";
import { useHidePillNav } from "@/components/system/BottomChrome";
import { useToast } from "@/components/ui/toast";
import { getWish, type WishBlockOut, type WishOut, type WishStatus } from "@/lib/api/wishlist";
import { findDiaryBySource } from "@/lib/api/diary";
import { cn } from "@/lib/utils/cn";
import { WISH_STATUS_LABEL } from "@/lib/i18n/wishlist";
import { BlockList } from "@/routes/wishes/components/WishBlocks";

const STATUS_LABELS: Record<WishStatus, string> = WISH_STATUS_LABEL;

const CATEGORY_LABELS: Record<string, string> = {
  general: "一般",
  savings: "儲蓄",
  travel: "旅行",
  event: "事件",
  purchase: "購物",
  bucket_list: "願望清單",
  custom: "自訂",
};

export function WishDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const wishId = id ?? "";
  const isMobile = useIsMobile();
  // W42 §7.9: this page docks `WishDetailActionBar` at `fixed bottom-0 z-30`,
  // and the PillNav is also `z-30` at `bottom-4` — so both rendered and the nav
  // sat **on top of** the action bar, covering its primary CTA.
  //
  // It is exactly the case `BottomChrome` was built for (W35.6, "create / edit
  // pages claim the bottom edge, so the PillNav is suppressed there and the
  // docked bar takes `bottom-0`"). This page just never claimed it: the
  // mechanism was written for the create/edit routes and `/wishes/:id` was
  // treated as a detail screen even though it ships a docked bar of its own.
  //
  // Found by `scripts/w42-route-audit.mjs`, which measures the last content row
  // against the nav on every route — 40.8px of the action bar was behind the
  // pill. Suppression is the declared claim rather than a `bottom` offset,
  // because the alternative is the two-bar strip W35.6 removed.
  useHidePillNav();
  const toast = useToast();
  // Dialog visibility is owned by the parent so the dialog only appears
  // when the user clicks "標為完成" — not on every page load. (The dialog's
  // internal `open = wish !== null` shortcut used to make the modal pop up
  // as soon as the wish finished loading.)
  const [completionOpen, setCompletionOpen] = useState(false);
  // Mobile-only: the active block for the log sheet. Tapping a budget /
  // progress block opens the sheet; tapping notes / checklist / link /
  // photo has no effect (per DATA_MODEL §5.5).
  const [logBlock, setLogBlock] = useState<WishBlockOut | null>(null);
  const [logSheetOpen, setLogSheetOpen] = useState(false);
  const onOpenLogSheet = (block: WishBlockOut) => {
    setLogBlock(block);
    setLogSheetOpen(true);
  };
  // Block anchor — `?block=:id` scroll + 1-second outline highlight.
  const blockAnchor = searchParams.get("block");
  const [highlightBlockId, setHighlightBlockId] = useState<string | null>(null);
  useEffect(() => {
    if (!blockAnchor) {
      setHighlightBlockId(null);
      return;
    }
    setHighlightBlockId(blockAnchor);
    const el = document.querySelector(`[data-block-anchor="${blockAnchor}"]`);
    if (el && "scrollIntoView" in el) {
      (el as HTMLElement).scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    const t = window.setTimeout(() => setHighlightBlockId(null), 1500);
    return () => window.clearTimeout(t);
  }, [blockAnchor, location.pathname]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["wishes", "detail", wishId],
    queryFn: () => getWish(wishId),
    enabled: Boolean(wishId),
    staleTime: 30_000,
  });

  // W50.3 D-3. This used to be a bare `fetch` with `credentials: "include"` and a
// hand-rolled `if (!res.ok) return []` — which swallows a 401 and a 500 into the
// same "no diaries" answer. Through the typed client it gets the CSRF/cookie
// funnel every other call uses and an error that is actually distinguishable.
const { data: linkedDiaries } = useQuery({
    queryKey: ["wishes", "linked-diaries", wishId],
    queryFn: async () => {
      const one = await findDiaryBySource("wishlist", wishId);
      // The endpoint returns a single Diary object (or null) — wrap it in an
      // array so the section can render a list. (W34.3 polish audit 2026-09-26.)
      return one ? [one] : [];
    },
    enabled: Boolean(wishId),
    staleTime: 60_000,
  });

  const wish: WishOut | undefined = data;
  const canComplete = wish?.status === "pending" || wish?.status === "in_progress";
  const completionWish = wish && canComplete ? wish : null;

  return (
    <PageErrorBoundary label="心願詳情">
      {/* W49.0 §6 — hero outside `PageContainer`; see `Wishes.tsx`. */}
      <div className="flex flex-col" data-testid="wish-detail-page">
        <PageHero
          // W52.0 D-9 — the trailing `ArrowLeft` chip becomes the trail's parent
          // segment. The chip was the only control on the mobile trailing (the
          // other two moved to the bottom action bar), so on a phone the title
          // row carried a back arrow and nothing else — while four other pages
          // spelled the same control four other ways.
          crumbs={[
            { to: "/wishes", label: "心願", testId: "wish-detail-back" },
            { label: wish?.name ?? "詳情" },
          ]}
          title={wish?.name ?? "心願詳情"}
          eyebrow={
            wish
              ? `${STATUS_LABELS[wish.status]} · ${CATEGORY_LABELS[wish.category] ?? wish.category}`
              : "載入中"
          }
          subtitle={
            wish ? `進度 ${wish.progress_percent}% · 區塊 ${wish.blocks.length}` : undefined
          }
          trailing={
            // W34.3.3: desktop keeps the original PageHero trailing (edit
            // layout + mark complete). Mobile moved both to the sticky bottom
            // action bar, and the back chip moved up into the trail.
            <div className="flex items-center gap-s-2 flex-wrap">
              {wish && !isMobile ? (
                <>
                  <button
                    type="button"
                    onClick={() => navigate(`/wishes/${wish.id}/edit-layout`)}
                    data-testid="wish-detail-edit-layout"
                    className="press inline-flex items-center gap-1.5 min-h-11 px-s-4 rounded-full border border-hairline text-ink-1"
                  >
                    編輯 layout
                  </button>
                  {canComplete ? (
                    <button
                      type="button"
                      onClick={() => setCompletionOpen(true)}
                      data-testid="wish-detail-mark-complete"
                      className="press inline-flex items-center gap-1.5 min-h-11 px-s-4 rounded-full bg-ink-1 text-paper text-body-sm font-semibold"
                    >
                      標為完成
                    </button>
                  ) : null}
                </>
              ) : null}
            </div>
          }
        />
        <PageContainer>
          {isLoading ? (
            <LoadingState shape="card" count={3} />
          ) : isError ? (
            <ErrorState
              title="讀取心願失敗"
              description={error instanceof Error ? error.message : "請稍後再試"}
              onRetry={() => refetch()}
            />
          ) : !wish ? (
            <ErrorState
              title="心願不存在"
              description="可能已被刪除"
              onRetry={() => navigate("/wishes")}
            />
          ) : (
            <div
              data-component="WishDetailLayout"
              data-testid="wish-detail-layout"
              className={cn(
                "flex flex-col gap-s-4",
                // W34.3.3: leave room for the sticky bottom action bar on
                // mobile. Desktop has no sticky bar so no extra padding.
                isMobile && "pb-[max(7rem,calc(7rem+env(safe-area-inset-bottom)))]",
              )}
            >
              <BlockList
                wish={wish}
                highlightBlockId={highlightBlockId}
                onOpenLogSheet={onOpenLogSheet}
                isMobile={isMobile}
              />
              <WishDiaryLinks
                wishId={wish.id}
                items={(linkedDiaries ?? []) as {
                  id: string;
                  title: string | null;
                  date: string;
                }[]}
              />
              {/* W35.2: 4 sub-resource panels + audit-log timeline
                (per docs/spec/wishlist/W35.2-SUBRESOURCE-UI-POLISH.md). */}
              <WishChatPanel wishId={wish.id} />
              <WishRemindersPanel wishId={wish.id} />
              <WishTimelinePanel wishId={wish.id} />
              <WishPhasesPanel wishId={wish.id} />
              <WishEventTimeline wishId={wish.id} />
            </div>
          )}
          <CompletionDialog
            wish={completionWish ? { id: completionWish.id, name: completionWish.name } : null}
            open={completionOpen}
            onOpenChange={setCompletionOpen}
            onCompleted={(w) => {
              // W34.2.2: toast on plain completion (per SPEC-DRIFT-CHECK
              // §13.2.5 follow-up). Order is mutation → toast → navigate
              // (per W34.2.2 anti-drift: 撕信 → PATCH resolve → toast push
              // → navigate).
              toast.push({
                message: `已完成「${w.name}」`,
                variant: "success",
              });
              navigate(`/wishes/archive?highlight=${w.id}`);
            }}
          />
          {/* Mobile-only: tap a budget / progress block to open the log
            sheet (W34.2.0). Desktop keeps the inline progress bar. */}
          <MobileLogSheet
            wishId={wishId}
            block={logBlock}
            open={logSheetOpen}
            onOpenChange={setLogSheetOpen}
          />
          {/* W34.3.3: mobile-only sticky bottom action bar. Hosts
            「標為完成」 + overflow menu (⋯). Desktop keeps the original
            PageHero trailing. The bar triggers the CompletionDialog
            (now BottomSheet on mobile per W34.3.0). */}
          {wish ? (
            <WishDetailActionBar
              wishId={wish.id}
              canComplete={canComplete}
              show={isMobile}
              onMarkComplete={() => setCompletionOpen(true)}
            />
          ) : null}
        </PageContainer>
      </div>
    </PageErrorBoundary>
  );
}

